/**
 * Media CapturedDate multi-stage extraction and fallback helper.
 *
 * Implements the authoritative priority chain:
 * 1. Image EXIF metadata (DateTimeOriginal, CreateDate, ModifyDate).
 * 2. Filename date extraction (YYYYMMDD or DDMMYYYY, with optional delimiters).
 *    - Synthesizes time from filename if present.
 *    - If no time in filename, combines the filename date with the time component from file creation/modification timestamp.
 * 3. File creation / modification timestamp (file.lastModified or birthtime).
 */

export interface IFileDateCandidate {
  name?: string;
  lastModified?: number;
  birthtimeMs?: number;
  creationTime?: number;
}

export interface IParsedDateResult {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}

/**
 * Validates whether year, month (1-12), and day (1-31) form a valid calendar date.
 * @param {number} year Four digit year.
 * @param {number} month Month 1-12.
 * @param {number} day Day 1-31.
 * @returns {boolean} True if valid calendar date between 1970 and 2099.
 */
export function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (year < 1970 || year > 2099) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/**
 * Validates hours, minutes, and seconds.
 * @param {number} hour 0-23.
 * @param {number} minute 0-59.
 * @param {number} [second=0] 0-59.
 * @returns {boolean} True if valid time.
 */
export function isValidTime(hour: number, minute: number, second: number = 0): boolean {
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 && second >= 0 && second <= 59;
}

/**
 * Parses time components from a string following a date in a filename.
 * Handles formats like:
 * - `_HHMMSS` (e.g. `_143022`)
 * - `_HHMM` (e.g. `_1430`)
 * - `_HH.MM.SS` / `_HH:MM:SS` / `-HH-MM-SS`
 * - `at HH.MM.SS`
 * @param {string} text The remaining substring after the date pattern.
 * @returns {{ hour: number; minute: number; second: number } | null}
 */
export function parseTimeFromFilename(text: string): { hour: number; minute: number; second: number } | null {
  if (!text) return null;

  // e.g. _143022 or -143022 or T143022
  const compactMatch = text.match(/^[T_\s-]?(\d{2})(\d{2})(\d{2})(?!\d)/);
  if (compactMatch) {
    const h = parseInt(compactMatch[1], 10);
    const m = parseInt(compactMatch[2], 10);
    const s = parseInt(compactMatch[3], 10);
    if (isValidTime(h, m, s)) return { hour: h, minute: m, second: s };
  }

  // e.g. _14:30:22, _14.30.22, at 14.30.22, -14-30-22
  const delimitedMatch = text.match(/(?:[T_\s-]|at\s+)?(\d{1,2})[:.-](\d{2})(?:[:.-](\d{2}))?/i);
  if (delimitedMatch) {
    const h = parseInt(delimitedMatch[1], 10);
    const m = parseInt(delimitedMatch[2], 10);
    const s = delimitedMatch[3] ? parseInt(delimitedMatch[3], 10) : 0;
    if (isValidTime(h, m, s)) return { hour: h, minute: m, second: s };
  }

  return null;
}

/**
 * Extracts a date (and optional time) from a filename string.
 * Supports:
 * - `YYYYMMDD` (e.g. `IMG_20240518_143022.jpg`, `20240518.png`, `2024-05-18`)
 * - `DDMMYYYY` (e.g. `18052024_family.jpg`, `18-05-2024.jpg`, `18_05_2024`)
 *
 * @param {string} fileName The file name to inspect.
 * @returns {IParsedDateResult | null} The parsed components or null.
 */
export function parseDateFromFilename(fileName: string): IParsedDateResult | null {
  if (!fileName || typeof fileName !== "string") return null;

  // Clean extension
  const baseName = fileName.replace(/\.[^/.]+$/, "");

  // 1. Try delimited DD[-_.]MM[-_.]YYYY (e.g., 18-05-2024 or 18_05_2024)
  const dmyDelimited = /(?:^|[^0-9])(\d{1,2})[-_.](\d{1,2})[-_.]((?:19|20)\d{2})(.*)$/;
  const matchDmyDelim = baseName.match(dmyDelimited);
  if (matchDmyDelim) {
    const d = parseInt(matchDmyDelim[1], 10);
    const m = parseInt(matchDmyDelim[2], 10);
    const y = parseInt(matchDmyDelim[3], 10);
    if (isValidCalendarDate(y, m, d)) {
      const time = parseTimeFromFilename(matchDmyDelim[4]);
      return {
        year: y,
        month: m,
        day: d,
        hour: time?.hour,
        minute: time?.minute,
        second: time?.second,
      };
    }
  }

  // 2. Try delimited YYYY[-_.]MM[-_.]DD (e.g., 2024-05-18 or 2024_05_18)
  const ymdDelimited = /(?:^|[^0-9])((?:19|20)\d{2})[-_.](\d{1,2})[-_.](\d{1,2})(.*)$/;
  const matchYmdDelim = baseName.match(ymdDelimited);
  if (matchYmdDelim) {
    const y = parseInt(matchYmdDelim[1], 10);
    const m = parseInt(matchYmdDelim[2], 10);
    const d = parseInt(matchYmdDelim[3], 10);
    if (isValidCalendarDate(y, m, d)) {
      const time = parseTimeFromFilename(matchYmdDelim[4]);
      return {
        year: y,
        month: m,
        day: d,
        hour: time?.hour,
        minute: time?.minute,
        second: time?.second,
      };
    }
  }

  // 3. Try compact 8-digit patterns: either YYYYMMDD or DDMMYYYY
  // Look for 8 consecutive digits bounded by non-digits
  const eightDigitRegex = /(?:^|[^0-9])(\d{8})(.*)$/g;
  let eightMatch: RegExpExecArray | null;
  while ((eightMatch = eightDigitRegex.exec(baseName)) !== null) {
    const digits = eightMatch[1];
    const suffix = eightMatch[2];

    // Try as YYYYMMDD
    const y1 = parseInt(digits.substring(0, 4), 10);
    const m1 = parseInt(digits.substring(4, 6), 10);
    const d1 = parseInt(digits.substring(6, 8), 10);
    const isYmd = isValidCalendarDate(y1, m1, d1);

    // Try as DDMMYYYY
    const d2 = parseInt(digits.substring(0, 2), 10);
    const m2 = parseInt(digits.substring(2, 4), 10);
    const y2 = parseInt(digits.substring(4, 8), 10);
    const isDmy = isValidCalendarDate(y2, m2, d2);

    // Disambiguation:
    // If y1 is between 1970-2099, YYYYMMDD takes precedence unless y1 > 31 and d2 <= 31 while y1 isn't a valid year
    if (isYmd) {
      const time = parseTimeFromFilename(suffix);
      return {
        year: y1,
        month: m1,
        day: d1,
        hour: time?.hour,
        minute: time?.minute,
        second: time?.second,
      };
    }

    if (isDmy) {
      const time = parseTimeFromFilename(suffix);
      return {
        year: y2,
        month: m2,
        day: d2,
        hour: time?.hour,
        minute: time?.minute,
        second: time?.second,
      };
    }
  }

  return null;
}

/**
 * Safely parses an EXIF date candidate into a Unix timestamp in milliseconds.
 *
 * Supports:
 * - Date objects
 * - Unix timestamp numbers
 * - Standard EXIF colon-formatted date strings: "YYYY:MM:DD HH:MM:SS" or "YYYY:MM:DD"
 * - Standard ISO or RFC strings parsed via Date.parse
 *
 * @param {Date | number | string | undefined | null} rawExifDate The raw EXIF date value.
 * @returns {number | undefined} Epoch milliseconds or undefined if invalid.
 */
export function parseExifDate(rawExifDate: Date | number | string | undefined | null): number | undefined {
  if (rawExifDate === null || rawExifDate === undefined) return undefined;
  if (rawExifDate instanceof Date && !isNaN(rawExifDate.getTime())) {
    const t = rawExifDate.getTime();
    if (t > 0) return t;
  }
  if (typeof rawExifDate === "number" && !isNaN(rawExifDate) && rawExifDate > 0) {
    return rawExifDate;
  }
  if (typeof rawExifDate === "string") {
    const trimmed = rawExifDate.trim();
    if (!trimmed) return undefined;

    // 1. Standard EXIF date format: "YYYY:MM:DD HH:MM:SS" (or "YYYY:MM:DD")
    const exifMatch = trimmed.match(/^(\d{4}):(\d{2}):(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (exifMatch) {
      const year = parseInt(exifMatch[1], 10);
      const month = parseInt(exifMatch[2], 10);
      const day = parseInt(exifMatch[3], 10);
      const hour = exifMatch[4] ? parseInt(exifMatch[4], 10) : 0;
      const minute = exifMatch[5] ? parseInt(exifMatch[5], 10) : 0;
      const second = exifMatch[6] ? parseInt(exifMatch[6], 10) : 0;
      if (isValidCalendarDate(year, month, day) && isValidTime(hour, minute, second)) {
        return new Date(year, month - 1, day, hour, minute, second).getTime();
      }
    }

    // 2. Standard ISO / RFC string
    const parsed = Date.parse(trimmed);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
}

/**
 * Extracts the authoritative CapturedDate (Unix timestamp in milliseconds) for a media item.
 *
 * Multi-Stage Execution:
 * 1. Image Metadata: If `rawExifDate` is provided and valid, return it.
 * 2. Filename Pattern: If filename has YYYYMMDD or DDMMYYYY:
 *    - If filename contains a time component, combine it.
 *    - If no time component in filename, combine the filename date with the time of day from file.lastModified.
 * 3. File Creation / Modification Timestamp: If file.lastModified / birthtime exists, return it.
 *
 * @param {IFileDateCandidate} file File candidate with optional name, lastModified, or birthtime.
 * @param {Date | number | string} [rawExifDate] Optional EXIF Date, number timestamp, or ISO string.
 * @returns {number | undefined} The extracted timestamp in milliseconds, or undefined if none found.
 */
export function extractCapturedDate(
  file: IFileDateCandidate,
  rawExifDate?: Date | number | string
): number | undefined {
  // Stage 1: Check image metadata EXIF
  const parsedExif = parseExifDate(rawExifDate);
  if (parsedExif !== undefined) {
    return parsedExif;
  }

  // Get file creation / modification timestamp if available
  const fileTimestamp =
    typeof file?.creationTime === "number" && file.creationTime > 0
      ? file.creationTime
      : typeof file?.birthtimeMs === "number" && file.birthtimeMs > 0
        ? file.birthtimeMs
        : typeof file?.lastModified === "number" && file.lastModified > 0
          ? file.lastModified
          : undefined;

  // Stage 2: Check filename pattern (YYYYMMDD or DDMMYYYY)
  if (file?.name) {
    const parsedFilenameDate = parseDateFromFilename(file.name);
    if (parsedFilenameDate) {
      const { year, month, day, hour, minute, second } = parsedFilenameDate;

      // If time was in the filename, use it
      if (hour !== null && hour !== undefined && minute !== null && minute !== undefined) {
        return new Date(year, month - 1, day, hour, minute, second ?? 0, 0).getTime();
      }

      // If no time was in the filename, combine date with the time component from file creation/modification time
      if (fileTimestamp !== null && fileTimestamp !== undefined) {
        const fileDateObj = new Date(fileTimestamp);
        return new Date(
          year,
          month - 1,
          day,
          fileDateObj.getHours(),
          fileDateObj.getMinutes(),
          fileDateObj.getSeconds(),
          fileDateObj.getMilliseconds()
        ).getTime();
      }

      // If no file time available either, return midnight of the filename date
      return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
    }
  }

  // Stage 3: Fall back to file creation/modification timestamp
  if (fileTimestamp !== null && fileTimestamp !== undefined) {
    return fileTimestamp;
  }

  return undefined;
}
